import { useState, useEffect } from 'react';
import {
  Dialog as MuiDialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  CircularProgress,
  Typography,
  Grid,
  Box,
  Alert,
} from '@mui/material';
import { useLang } from '../../contexts/langContext';
import { getLicenseStatus } from '../../services/license';

function LicenseInfoDialog({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const { lang } = useLang();
  const [licenseData, setLicenseData] = useState<{
    clientName: string;
    maxUsers: number;
    expireDate: string;
    issuedAt: string;
    daysRemaining: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setError(null);
      setLicenseData(null);
      getLicenseStatus()
        .then(data => {
          setLicenseData(data);
        })
        .catch(err => {
          console.error('Error fetching license data:', err);
          setError(lang === 'en' ? 'License information unavailable' : '许可证信息不可用');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [isOpen, lang]);

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }
      return date.toLocaleDateString(lang === 'en' ? 'en-US' : 'zh-CN');
    } catch (e) {
      console.error("Error formatting date:", e);
      return 'Invalid Date';
    }
  };

  return (
    <MuiDialog open={isOpen} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {lang === 'en' ? 'License Information' : '许可证信息'}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {lang === 'en' ? 'Current license details' : '当前许可证详情'}
        </DialogContentText>
        
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        
        {error && (
           <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>
        )}

        {licenseData && !loading && !error && (
          <Box sx={{ py: 2 }}>
             <Grid container spacing={1}>
              <Grid item xs={5}><Typography variant="body2" color="text.secondary">{lang === 'en' ? 'Client' : '客户'}:</Typography></Grid>
              <Grid item xs={7}><Typography variant="body2">{licenseData.clientName}</Typography></Grid>
              
              <Grid item xs={5}><Typography variant="body2" color="text.secondary">{lang === 'en' ? 'User Limit' : '用户限制'}:</Typography></Grid>
              <Grid item xs={7}><Typography variant="body2">{licenseData.maxUsers}</Typography></Grid>
              
              <Grid item xs={5}><Typography variant="body2" color="text.secondary">{lang === 'en' ? 'Issue Date' : '签发日期'}:</Typography></Grid>
              <Grid item xs={7}><Typography variant="body2">{formatDate(licenseData.issuedAt)}</Typography></Grid>
              
              <Grid item xs={5}><Typography variant="body2" color="text.secondary">{lang === 'en' ? 'Expiry Date' : '到期日期'}:</Typography></Grid>
              <Grid item xs={7}><Typography variant="body2">{formatDate(licenseData.expireDate)}</Typography></Grid>
              
              <Grid item xs={5}><Typography variant="body2" color="text.secondary">{lang === 'en' ? 'Days Remaining' : '剩余天数'}:</Typography></Grid>
              <Grid item xs={7}>
                 <Typography 
                   variant="body2" 
                   sx={{ color: licenseData.daysRemaining < 30 ? 'error.main' : 'text.primary' }}
                 >
                   {licenseData.daysRemaining}
                 </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>
    </MuiDialog>
  );
}

export default LicenseInfoDialog;